import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { getData, storeData } from '../utils/sessionStorage';
import { AppConstants, TrackValues } from '../utils/constants';
import EnrollmentContextProvider from '../contexts/EnrollmentContext';
import JobProfileForm from '../components/EnrollmentForm/JobProfileForm';
import PrimaryTrackForm from '../components/EnrollmentForm/PrimaryTrackForm';
import SoftwareEngineeringForm from '../components/EnrollmentForm/SoftwareEngineeringForm';
import WebDevelopmentForm from '../components/EnrollmentForm/WebDevelopmentForm';
import DataScienceForm from '../components/EnrollmentForm/DataScienceForm';
import DesignForm from '../components/EnrollmentForm/DesignForm';
import MarketingForm from '../components/EnrollmentForm/MarketingForm';
import SalesForm from '../components/EnrollmentForm/SalesForm';
import EducationEmploymentForm from '../components/EnrollmentForm/EducationEmploymentForm';
import EqualEmploymentOpportunityForm from '../components/EnrollmentForm/EqualEmploymentOpportunityForm';
import SlowEnrollmentCompletion from './SlowEnrollmentCompletion';
import LoadingScreen from '../components/EnrollmentForm/LoadingScreen';
import styles from '../styles/EnrollmentPage.module.scss';

const EnrollmentPage = ({ page, userEmail, slowForm }) => {
  const { Provider } = EnrollmentContextProvider;

  const [currentPage, setCurrentPage] = useState(getData(AppConstants.currentPage) || 1);
  const [primaryTrack, setPrimaryTrack] = useState(getData(AppConstants.primaryTrack));
  const storePrimaryTrack = (track) => {
    setPrimaryTrack(track);
    storeData(AppConstants.primaryTrack, track);
  };
  const successUrl = 'https://www.pathrise.com/thankyou';

  // Must have user email in order to advance form
  if (!userEmail) {
    return <Redirect to="/" />;
  }

  // Cannot advance past uncompleted pages
  if (page > currentPage) {
    return <Redirect to={`/${currentPage}`} />;
  }

  const { SWE, WebDev, Data, Design, Marketing, Sales } = TrackValues;

  const renderTrackDetailsPage = (track) => {
    switch (track) {
      case SWE:
        return <SoftwareEngineeringForm />;
      case WebDev:
        return <WebDevelopmentForm />;
      case Data:
        return <DataScienceForm />;
      case Design:
        return <DesignForm />;
      case Marketing:
        return <MarketingForm />;
      case Sales:
        return <SalesForm />;
      default:
        if (page === currentPage) {
          setCurrentPage(currentPage + 1);
          storeData(AppConstants.currentPage, currentPage + 1);
        }
        return <Redirect to={`/${page + 1}`} />;
    }
  };

  return (
    <Provider
      value={{
        userEmail,
        page,
        slowForm,
        setCurrentPage,
        maxPage: AppConstants.totalPages,
      }}
    >
      <main className={styles.EnrollmentPage}>
        {(() => {
          switch (page) {
            case 1:
              return <JobProfileForm />;
            case 2:
              return <PrimaryTrackForm setPrimaryTrack={storePrimaryTrack} />;
            case 3:
              return <EducationEmploymentForm primaryTrack={primaryTrack} />;
            case 4:
              return renderTrackDetailsPage(primaryTrack);
            case 5:
              return <EqualEmploymentOpportunityForm />;
            case 6:
              if (slowForm) {
                return <SlowEnrollmentCompletion />;
              }
              window.location.href = successUrl;
              return <LoadingScreen />;
            default:
              return <Redirect to={`/${currentPage}`} />;
          }
        })()}
      </main>
    </Provider>
  );
};

EnrollmentPage.defaultProps = {
  userEmail: null,
  slowForm: false,
};

EnrollmentPage.propTypes = {
  page: PropTypes.number.isRequired,
  userEmail: PropTypes.string,
  slowForm: PropTypes.bool,
};

export default EnrollmentPage;
